'use client';

import { useState } from 'react';
import { Box, Button, Flex, Input, Text } from '@chakra-ui/react';
import { InlineFilterGroup } from './InlineFilterControls';
import type { PromptSubtopic } from '@/types/database-schema';
import { MAX_SUBTOPICS_PER_GROUP, MAX_TOPIC_NAME } from '@/lib/prompt-topics/contract';

/** Optional children always use the current parent's prompt inventory. */
export function PromptSubtopicEditor({
  subtopics,
  prompts,
  parentPromptIds,
  onChange,
  disabled = false,
  createId = () => crypto.randomUUID(),
}: {
  subtopics: PromptSubtopic[];
  prompts: Array<{ prompt_id: string; text?: string | null }>;
  parentPromptIds?: string[];
  onChange: (children: PromptSubtopic[]) => void;
  disabled?: boolean;
  createId?: () => string;
}) {
  const [selectedId, setSelectedId] = useState('');
  const [name, setName] = useState('');
  const [search, setSearch] = useState('');
  const child = subtopics.find(item => item.id === selectedId);
  const parentIds = new Set(parentPromptIds ?? prompts.map(prompt => prompt.prompt_id));
  const invalidCount = subtopics.reduce(
    (count, item) => count + item.prompt_ids.filter(id => !parentIds.has(id)).length,
    0
  );
  const duplicate = subtopics.some(
    item =>
      item !== child &&
      item.name.trim().toLocaleLowerCase('en-US') === name.trim().toLocaleLowerCase('en-US')
  );
  return (
    <Box borderTopWidth="1px" borderColor="ui.border.subtle" pt="3">
      <Text fontSize="sm" fontWeight="600" mb="2">
        Subtopics (optional)
      </Text>
      <Text fontSize="xs" color="ui.text.secondary" mb="2">
        Assign prompts from this topic. Prompts may belong to multiple subtopics.
      </Text>
      <Flex gap="2" align="center" flexWrap="wrap">
        <InlineFilterGroup
          label="Subtopic"
          options={[
            { value: '', label: 'New subtopic' },
            ...subtopics.map(item => ({ value: item.id, label: item.name })),
          ]}
          value={selectedId}
          onChange={id => {
            setSelectedId(id);
            setName(subtopics.find(item => item.id === id)?.name || '');
            setSearch('');
          }}
          disabled={disabled}
        />
        <Input
          aria-label="Subtopic name"
          value={name}
          maxLength={MAX_TOPIC_NAME}
          size="sm"
          width="220px"
          disabled={disabled}
          onChange={event => setName(event.target.value)}
        />
        <Button
          size="xs"
          disabled={
            disabled ||
            !name.trim() ||
            duplicate ||
            (!child && subtopics.length >= MAX_SUBTOPICS_PER_GROUP)
          }
          onClick={() => {
            const next = name.trim();
            if (child)
              onChange(subtopics.map(item => (item === child ? { ...item, name: next } : item)));
            else {
              const id = createId();
              onChange([...subtopics, { id, name: next, prompt_ids: [] }]);
              setSelectedId(id);
            }
            setName(next);
          }}
        >
          {child ? 'Rename subtopic' : 'Create subtopic'}
        </Button>
        {child ? (
          <Button
            size="xs"
            variant="ghost"
            disabled={disabled}
            onClick={() => {
              onChange(subtopics.filter(item => item !== child));
              setSelectedId('');
              setName('');
            }}
          >
            Remove subtopic
          </Button>
        ) : null}
      </Flex>
      {invalidCount > 0 ? (
        <Box mt="2" role="alert">
          <Text fontSize="xs" color="ui.text.secondary">
            {invalidCount} subtopic assignments no longer belong to this topic. Remove these
            assignments before saving.
          </Text>
          <Button
            size="xs"
            mt="1"
            disabled={disabled}
            onClick={() =>
              onChange(
                subtopics.map(item => ({
                  ...item,
                  prompt_ids: item.prompt_ids.filter(id => parentIds.has(id)),
                }))
              )
            }
          >
            Remove unassigned prompts
          </Button>
        </Box>
      ) : null}
      {child ? (
        <>
          <Text fontSize="xs" color="ui.text.secondary" my="2">
            {child.prompt_ids.length} prompts · {subtopics.length} / {MAX_SUBTOPICS_PER_GROUP}{' '}
            subtopics
          </Text>
          <Input
            aria-label="Search subtopic prompts"
            placeholder="Search topic prompts"
            value={search}
            onChange={event => setSearch(event.target.value)}
            size="sm"
            mb="2"
            disabled={disabled}
          />
          <Box maxH="220px" overflowY="auto">
            {prompts
              .filter(prompt =>
                (prompt.text || '').toLocaleLowerCase().includes(search.toLocaleLowerCase())
              )
              .map(prompt => (
                <label key={prompt.prompt_id} style={{ display: 'flex', gap: 8, padding: '4px 0' }}>
                  <input
                    type="checkbox"
                    checked={child.prompt_ids.includes(prompt.prompt_id)}
                    disabled={disabled}
                    onChange={event =>
                      onChange(
                        subtopics.map(item =>
                          item === child
                            ? {
                                ...item,
                                prompt_ids: event.target.checked
                                  ? [...item.prompt_ids, prompt.prompt_id]
                                  : item.prompt_ids.filter(id => id !== prompt.prompt_id),
                              }
                            : item
                        )
                      )
                    }
                  />
                  <Text fontSize="sm">{prompt.text || 'Prompt text unavailable'}</Text>
                </label>
              ))}
            {!prompts.length ? (
              <Text fontSize="xs" color="ui.text.secondary">
                Assign prompts to the topic to add them here. This subtopic may stay empty.
              </Text>
            ) : null}
          </Box>
        </>
      ) : null}
    </Box>
  );
}
